import React, { useMemo } from 'react'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix-fetcher'

const BasketTotal = ({ instance, event, pendingMealDealSelection, pendingTickets, seatActions, ticketOptions, mealDealsToRemove, commissionValue = 0 }) => {
  // Fetch current basket to get all tickets including meal deals
  const basketUrl = `/basket`
  const { data: basketContents } = useSWR(basketUrl, SpektrixFetcher)
  
  // Get meal deals from instance data
  const mealDeals = instance.meal_deals || []
  const mealDealInstanceIds = mealDeals.map(md => md.spektrix_v3_id || md.id)
  
  // Calculate totals
  const totals = useMemo(() => {
    if (!basketContents?.tickets) {
      return {
        mainTicketTotal: 0,
        mealDealTotal: 0,
        grandTotal: 0,
        mainTicketCount: 0,
        mealDealCount: 0
      }
    }
    
    // Filter tickets for this instance
    const mainTickets = basketContents.tickets.filter(
      t => t.instance.id === (instance.spektrix_v3_id || instance.id)
    )
    
    // Filter meal deal tickets - check both instance.id and spektrix_v3_id
    // Also check if the ticket's event name matches meal deal events
    const mealDealEventNames = mealDeals.map(md => md.event_name).filter(Boolean)
    const mealDealTickets = basketContents.tickets.filter(t => {
      // Check by instance ID
      const idMatch = mealDealInstanceIds.includes(t.instance.id) || 
                     mealDealInstanceIds.includes(t.instance.spektrix_v3_id)
      // Check by event name if available
      const nameMatch = mealDealEventNames.length > 0 && 
                       mealDealEventNames.some(name => 
                         t.event?.name?.includes(name) || 
                         t.instance?.event?.name?.includes(name)
                       )
      return idMatch || nameMatch
    })
    
    // Calculate totals (amount might be in pence or pounds depending on API)
    const getTicketPrice = (ticket, includeCommission = true) => {
      // Check various price structures
      const amount = ticket.price?.amount || ticket.amount || ticket.price || 0
      // Amount is always in pounds from Spektrix API v3
      const basePrice = Number(amount) || 0
      // Add commission if applicable
      return includeCommission ? basePrice + parseFloat(commissionValue || 0) : basePrice
    }
    
    // Calculate main ticket total with pending changes
    let mainTicketTotal = 0
    let ticketsToRemove = []
    
    // If we have pending tickets info, use that for calculation
    if (pendingTickets && pendingTickets.length > 0 && ticketOptions) {
      // Calculate based on pending tickets (which includes type changes)
      pendingTickets.forEach(seat => {
        // Skip if marked for removal
        if (seatActions?.[seat.id]?.removeFromBasket) {
          ticketsToRemove.push(seat)
          return
        }
        
        // Check if there's a seat action that modifies the ticket type
        const seatAction = seatActions?.[seat.id]
        let ticketTypeId = null
        let bandId = null
        
        if (seatAction?.ticket?.type?.id) {
          // Use the modified ticket type from seat actions
          ticketTypeId = seatAction.ticket.type.id
          bandId = seatAction.ticket.band?.id
        } else if (seat.ticket?.type?.id) {
          // Use the original ticket type
          ticketTypeId = seat.ticket.type.id
          bandId = seat.ticket.band?.id
        }
        
        if (ticketTypeId) {
          // Find the matching ticket option to get the price
          const matchingOption = ticketOptions.find(option => 
            option.value === ticketTypeId && 
            (!bandId || option.band?.id === bandId)
          )
          
          if (matchingOption?.ticket?.amount) {
            mainTicketTotal += Number(matchingOption.ticket.amount) || 0
          } else {
            // Fallback to original ticket price if available
            mainTicketTotal += seat.ticket?.amount || seat.ticket?.price || 0
          }
        }
      })
    } else {
      // Fall back to current basket calculation
      mainTicketTotal = mainTickets.reduce((sum, ticket) => sum + getTicketPrice(ticket), 0)
    }
    // Filter out meal deals marked for removal
    // Convert proxy to array if needed
    const mealDealsToRemoveArray = mealDealsToRemove ? Array.from(mealDealsToRemove) : []
    
    
    const mealDealTicketsAfterRemoval = mealDealsToRemoveArray.length > 0 ? 
      mealDealTickets.filter(ticket => !mealDealsToRemoveArray.includes(ticket.id)) :
      mealDealTickets
    
    const mealDealTotal = mealDealTicketsAfterRemoval.reduce((sum, ticket) => sum + getTicketPrice(ticket, false), 0)
    const removedMealDealCount = mealDealTickets.length - mealDealTicketsAfterRemoval.length
    const removedMealDealTotal = mealDealTickets
      .filter(ticket => mealDealsToRemoveArray.includes(ticket.id))
      .reduce((sum, ticket) => sum + getTicketPrice(ticket, false), 0)
    
    // Calculate pending meal deals if explicitly selected
    const pendingMealDealCount = pendingMealDealSelection?.count || 0
    const pendingMealDealTotal = pendingMealDealSelection ? 
      (pendingMealDealSelection.ticketType?.ticket?.amount || 0) * pendingMealDealCount : 
      0
    
    return {
      mainTicketTotal,
      mealDealTotal,
      grandTotal: mainTicketTotal + mealDealTotal,
      mainTicketCount: mainTickets.length,
      mealDealCount: mealDealTicketsAfterRemoval.length,
      pendingMealDealCount,
      pendingMealDealTotal,
      removedMealDealCount,
      removedMealDealTotal,
      totalWithPending: mainTicketTotal + mealDealTotal + pendingMealDealTotal
    }
  }, [basketContents, instance, mealDealInstanceIds, pendingMealDealSelection, pendingTickets, seatActions, ticketOptions, mealDealsToRemove, commissionValue])
  
  // Don't show if loading or no tickets at all (but show if we have pending tickets)
  if (!basketContents || (totals.mainTicketCount === 0 && totals.mealDealCount === 0 && (!pendingTickets || pendingTickets.length === 0))) {
    return null
  }
  
  return (
    <div className='mt-6 p-4 bg-blue-50 dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-600'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          Total for this event
        </h3>
        <span className='text-xs text-gray-500 dark:text-gray-400'>
          (not full basket)
        </span>
      </div>
      
      <div className='space-y-2'>
        <div className='flex justify-between text-sm'>
          <span className='text-gray-600 dark:text-gray-400'>
            {pendingTickets && pendingTickets.length > 0 ? 
              pendingTickets.filter(s => !seatActions?.[s.id]?.removeFromBasket).length :
              totals.mainTicketCount
            } ticket{(pendingTickets && pendingTickets.length > 0 ? 
              pendingTickets.filter(s => !seatActions?.[s.id]?.removeFromBasket).length :
              totals.mainTicketCount) !== 1 ? 's' : ''}
          </span>
          <span className='font-medium text-gray-900 dark:text-white'>
            £{totals.mainTicketTotal.toFixed(2)}
          </span>
        </div>
        
        {totals.mealDealCount > 0 && (
          <div className='flex justify-between text-sm'>
            <span className='text-gray-600 dark:text-gray-400'>
              {totals.mealDealCount} meal deal{totals.mealDealCount !== 1 ? 's' : ''}
            </span>
            <span className='font-medium text-gray-900 dark:text-white'>
              £{totals.mealDealTotal.toFixed(2)}
            </span>
          </div>
        )}
        
        {totals.pendingMealDealCount > 0 && (
          <div className='flex justify-between text-sm italic'>
            <span className='text-gray-500 dark:text-gray-400'>
              {totals.pendingMealDealCount} meal deal{totals.pendingMealDealCount !== 1 ? 's' : ''} to be added
            </span>
            <span className='text-gray-500 dark:text-gray-400'>
              +£{totals.pendingMealDealTotal.toFixed(2)}
            </span>
          </div>
        )}
        
        <div className='pt-2 mt-2 border-t border-gray-200 dark:border-gray-600'>
          <div className='flex justify-between'>
            <span className='text-lg font-semibold text-gray-900 dark:text-white'>
              Event Total
            </span>
            <span className='text-2xl font-bold text-blue-600 dark:text-blue-400'>
              £{totals.grandTotal.toFixed(2)}
            </span>
          </div>
          {totals.pendingMealDealCount > 0 && (
            <div className='flex justify-between mt-1'>
              <span className='text-sm text-gray-600 dark:text-gray-400'>
                Total after update
              </span>
              <span className='text-lg font-semibold text-green-600 dark:text-green-400'>
                £{totals.totalWithPending.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div className='mt-3 pt-3 border-t border-gray-200 dark:border-gray-600'>
        <p className='text-xs text-gray-500 dark:text-gray-400'>
          This total is for {event?.name || 'this event'} only
        </p>
      </div>
    </div>
  )
}

export default BasketTotal